import { observer } from 'mobx-react-lite'
import React from 'react'
import { Space, Form, DatePicker, Select, Checkbox, TreeSelect } from 'antd'
import dayjs from 'dayjs'
import TimesRange from '../TimesRange'
import Title from 'antd/es/typography/Title'
import { recurrenceLabels } from './labels'
import { GeneralTourTitle } from '../../Shared/GeneralComponents'
import { getTourArrayByKeys } from '../../../stores/tour/config'
import { useStores } from '../../../cls/stores/MobXProvider'
import { InfoCircleOutlined } from '@ant-design/icons'
import { translate as t } from '../../../cls/TranslationContext'

const DaysOfMonth = observer(({ value, onChange }) => {
	return (
		<TreeSelect
			value={value}
			onChange={onChange}
			style={{
				width: '100%'
			}}
			dropdownStyle={{
				maxHeight: 400,
				overflow: 'auto'
			}}
			treeData={[
				{
					title: t('single.first'),
					value: 'first-group',
					disabled: true,
					children: [
						{
							title: t('single.first-day-of-month'),
							value: 'first'
						},
						{
							title: t('single.first-work-day'),
							value: 'first-work-day'
						},
						{
							title: t('_day.week-days'),
							value: 'first-week-days',
							disabled: true,
							children: [
								{
									title: t('single.first-monday'),
									value: 'first-monday'
								},
								{
									title: t('single.first-tuesday'),
									value: 'first-tuesday'
								},
								{
									title: t('single.first-wednesday'),
									value: 'first-wednesday'
								},
								{
									title: t('single.first-thursday'),
									value: 'first-thursday'
								},
								{
									title: t('single.first-friday'),
									value: 'first-friday'
								},
								{
									title: t('single.first-saturday'),
									value: 'first-saturday'
								},
								{
									title: t('single.first-sunday'),
									value: 'first-sunday'
								}
							]
						}
					]
				},
				{
					title: t('single.last'),
					value: 'last-group',
					disabled: true,
					children: [
						{
							title: t('single.last-day-of-month'),
							value: 'last'
						},
						{
							title: t('single.last-work-day'),
							value: 'last-work-day'
						},
						{
							title: t('_day.week-days'),
							value: 'last-week-days',
							disabled: true,
							children: [
								{
									title: t('single.last-monday'),
									value: 'last-monday'
								},
								{
									title: t('single.last-tuesday'),
									value: 'last-tuesday'
								},
								{
									title: t('single.last-wednesday'),
									value: 'last-wednesday'
								},
								{
									title: t('single.last-thursday'),
									value: 'last-thursday'
								},
								{
									title: t('single.last-friday'),
									value: 'last-friday'
								},
								{
									title: t('single.last-saturday'),
									value: 'last-saturday'
								},
								{
									title: t('single.last-sunday'),
									value: 'last-sunday'
								}
							]
						}
					]
				}
			]}
			placeholder={t('__select.please')}
			treeDefaultExpandAll
		/>
	)
})

export const RecurrenceFormItems = observer(({ form }) => {
	const { tourStore } = useStores()

	const recurrenceTypeTrack = Form.useWatch('recurrenceType', form)
	const recurrenceStartEndTrack = Form.useWatch('recurrenceStartEnd', form)

	const weekDaysOptions = [
		{ label: t('single.monday'), value: 'monday' },
		{ label: t('single.tuesday'), value: 'tuesday' },
		{ label: t('single.wednesday'), value: 'wednesday' },
		{ label: t('single.thursday'), value: 'thursday' },
		{ label: t('single.friday'), value: 'friday' },
		{ label: t('single.saturday'), value: 'saturday' },
		{ label: t('single.sunday'), value: 'sunday' }
	]

	// We start months from 0, so we can match dayJs month numbers
	const allMonthsOptions = [
		{ label: t('single.january'), value: '0' },
		{ label: t('single.february'), value: '1' },
		{ label: t('single.march'), value: '2' },
		{ label: t('single.april'), value: '3' },
		{ label: t('single.may'), value: '4' },
		{ label: t('single.june'), value: '5' },
		{ label: t('single.july'), value: '6' },
		{ label: t('single.august'), value: '7' },
		{ label: t('single.september'), value: '8' },
		{ label: t('single.october'), value: '9' },
		{ label: t('single.november'), value: '10' },
		{ label: t('single.december'), value: '11' }
	]

	const filteredMonthsOptions = recurrenceStartEndTrack
		? allMonthsOptions.filter((month) => {
				const start = dayjs(recurrenceStartEndTrack[0])
				const end = dayjs(recurrenceStartEndTrack[1])
				const monthIndex = allMonthsOptions.findIndex(
					(opt) => opt.value === month.value
				)
				const startMonthIndex = start.month()
				const endMonthIndex = end.month()
				const startYear = start.year()
				const endYear = end.year()

				// If start and end are in the same year
				if (startYear === endYear) {
					return (
						monthIndex >= startMonthIndex &&
						monthIndex <= endMonthIndex
					)
				}

				// If the current month is in the start year
				if (
					start.year() === startYear &&
					monthIndex >= startMonthIndex
				) {
					return true
				}

				// If the current month is in the end year
				if (end.year() === endYear && monthIndex <= endMonthIndex) {
					return true
				}

				// If the date range spans multiple years and the current month is in between
				if (startYear < endYear) {
					return true
				}

				return false
		  })
		: allMonthsOptions

	// eslint-disable-next-line react/prop-types
	const UseSingleDateOrRange = ({ recurrenceType, ...props }) => {
		// eslint-disable-next-line react/prop-types
		const { value, onChange, ...rest } = props
		return recurrenceType === 'deadline' ? (
			<DatePicker
				{...rest}
				value={value && value[0]}
				onChange={(value) => {
					form.setFieldsValue({
						recurrenceStartEnd: [value]
					})
					onChange(value)
				}}
			/>
		) : (
			<DatePicker.RangePicker
				allowEmpty
				placeholder={[t('single.start-date'), t('single.end-date')]}
				value={value}
				onChange={(value) => {
					// however, end date cant be same day as start date
					const [start, end] = value || []
					if (start && end && start.isSame(end, 'day')) {
						value[1] = end.add(1, 'day')
						alert(t('single.end-date-not-same'))
					} else if (start && !end) {
						value[1] = start.add(1, 'day')
					}

					// Fallback and recheck the start and end dates, make sure they are not the same day
					const [startDay, endDay] = value || []
					if (startDay && endDay && startDay.isSame(endDay, 'day')) {
						alert(t('single.end-date-not-same'))
						value[1] = startDay.add(1, 'day')
					}
					form.setFieldsValue({
						recurrenceStartEnd: value || []
					})
					onChange(value || [])
				}}
				{...rest}
			/>
		)
	}

	/*
	 * Tour Reccurence
	 * This is a tour for the recurrence fields. Manually added to the tour due to possible scenario were list item might not have all recurrence types.
	 */
	let tourRecurrence = []

	if (recurrenceTypeTrack === 'week') {
		tourRecurrence = getTourArrayByKeys('recurrence', [
			'recurrencedaysofweek'
		])
	} else if (recurrenceTypeTrack === 'month') {
		tourRecurrence = getTourArrayByKeys('recurrence', [
			'recurrencedayofmonth'
		])
	} else if (recurrenceTypeTrack == 'year') {
		tourRecurrence = getTourArrayByKeys('recurrence', [
			'recurrencemonths',
			'recurrencedayofmonth'
		])
	}

	const startTour = () => {
		tourStore.startTour('Recurrence-tour', [
			// Recurrence
			...getTourArrayByKeys('recurrence', [
				'section',
				'recurrencenext',
				'recurrencetype',
				'recurrencestartend'
			]),
			...tourRecurrence,
			...getTourArrayByKeys('recurrence', ['recurrencetimes'])
		])
	}

	return (
		<Space
			id="target-recurrenceformitems"
			direction="vertical"
			style={{ width: '100%' }}
		>
			<GeneralTourTitle
				align="end"
				titleProps={{
					text: t('_recurrence'),
					h: Title,
					level: 5
				}}
				buttonProps={{
					onClick: startTour,
					disabled: false,
					style: { marginBottom: 4 }
				}}
			/>
			<div id="target-recurrenceformitems-recurrencenext">
				<Form.Item
					label={t('single.next')}
					//tooltip="Next iteration of the sprint."
					tooltip={{
						title: t('single.next-sprint'),
						icon: <InfoCircleOutlined />
					}}
					name="recurrenceNext"
				>
					<DatePicker
						placeholder={t('__select.item%single.date')}
						style={{ width: '100%' }}
						disabled={true}
						format="DD/MM/YYYY"
					/>
				</Form.Item>
			</div>
			<Space style={{ width: '100%' }}>
				<div id="target-recurrenceformitems-recurrencetype">
					<Form.Item
						label={t('_recurrence')}
						//tooltip="Recurrence of the sprint."
						tooltip={{
							title: t('__of-the%_recurrence%_sprint'),
							icon: <InfoCircleOutlined />
						}}
						name="recurrenceType"
						rules={[
							{
								required: true,
								message: t('_recurrence.please')
							}
						]}
					>
						<Select
							placeholder={t('__select.item%_recurrence')}
							allowClear
							options={[
								...Object.keys(recurrenceLabels).map((key) => ({
									label: recurrenceLabels[key],
									value: key
								}))
							]}
							onChange={() => {
								form.setFieldsValue({
									recurrenceStartEnd: [],
									recurrenceMonthsTrack: [],
									recurrenceDaysOfWeek: [],
									recurrenceDayOfMonth: '',
									recurrenceMonths: [],
									recurrenceTimes: []
								})
							}}
						/>
					</Form.Item>
				</div>
				<div id="target-recurrenceformitems-recurrencestartend">
					<Form.Item
						label={
							recurrenceTypeTrack !== 'deadline'
								? t('single.start-end.label')
								: t('single.deadline')
						}
						//tooltip="Start and end date of the sprint."
						tooltip={{
							title: t('single.start-end.description%_sprint'),
							icon: <InfoCircleOutlined />
						}}
						name="recurrenceStartEnd"
						rules={[
							{
								required: true,
								message: t('single.start-end.please')
							}
						]}
					>
						<UseSingleDateOrRange
							recurrenceType={recurrenceTypeTrack}
							style={{ width: '100%' }}
							presets={
								recurrenceTypeTrack !== 'deadline' && [
									{
										label: (
											<span
												aria-label={t(
													'single.now-to-eod'
												)}
											>
												{t('single.now-eod')}
											</span>
										),
										value: () => [
											dayjs(),
											dayjs().endOf('day')
										] // 5.8.0+ support function
									},
									{
										label: (
											<span
												aria-label={t(
													'single.now-to-eod'
												)}
											>
												{t('single.now-eod')}
											</span>
										),
										value: () => [
											dayjs(),
											dayjs().endOf('week')
										]
									},
									{
										label: (
											<span
												aria-label={t(
													'single.now-to-eom'
												)}
											>
												{t('single.now-eom')}
											</span>
										),
										value: () => [
											dayjs(),
											dayjs().endOf('month')
										]
									},
									{
										label: (
											<span
												aria-label={t(
													'single.curent-time-plus-30-days'
												)}
											>
												{t(
													'single.curent-date-plus-30-days'
												)}
											</span>
										),
										value: () => [
											dayjs(),
											dayjs().add(30, 'day')
										]
									},
									{
										label: (
											<span
												aria-label={t(
													'single.now-to-year-end'
												)}
											>
												{t('single.now-eoy')}
											</span>
										),
										value: () => [
											dayjs(),
											dayjs().endOf('year')
										]
									}
								]
							}
							allowClear
							format="DD/MM/YYYY"
							minDate={dayjs().startOf('day')}
							onChange={(val) => {
								// When the recurrence type is 'deadline', update the recurrenceStartEnd field with the selected date
								if (recurrenceTypeTrack === 'deadline') {
									form.setFieldsValue({
										recurrenceStartEnd: [val]
									})
								}

								form.setFieldsValue({
									recurrenceMonths: [],
									recurrenceDaysOfWeek: [],
									recurrenceDayOfMonth: ''
								})
							}}
							showTime={recurrenceTypeTrack === 'deadline'}
							picker={
								recurrenceTypeTrack === 'deadline'
									? 'date'
									: recurrenceTypeTrack === 'day'
									? 'date'
									: recurrenceTypeTrack === 'week'
									? 'week'
									: recurrenceTypeTrack === 'month'
									? 'month'
									: recurrenceTypeTrack === 'year' && 'year'
							}
						/>
					</Form.Item>
				</div>
			</Space>
			{recurrenceTypeTrack !== 'deadline' && (
				<>
					{recurrenceTypeTrack === 'week' && (
						<div id="target-recurrenceformitems-recurrencedaysofweek">
							<Form.Item
								label={t('single.day-of-week')}
								//tooltip="Select the days of the week when this sprint should recur."
								tooltip={{
									title: t('single.select-days-of-week'),
									icon: <InfoCircleOutlined />
								}}
								name="recurrenceDaysOfWeek"
							>
								<Checkbox.Group options={weekDaysOptions} />
							</Form.Item>
						</div>
					)}
					{recurrenceTypeTrack === 'month' && (
						<div id="target-recurrenceformitems-recurrencedayofmonth">
							<Form.Item
								label={t('single.day-of-month')}
								//tooltip="Select the day of the month when this sprint should recur."
								tooltip={{
									title: t('single.select-day-of-month'),
									icon: <InfoCircleOutlined />
								}}
								name="recurrenceDayOfMonth"
								rules={[
									{
										required: true,
										message: t(
											'single.days-of-month-required'
										)
									}
								]}
							>
								<DaysOfMonth />
							</Form.Item>
						</div>
					)}
					{recurrenceTypeTrack === 'year' && (
						<>
							<div id="target-recurrenceformitems-recurrencemonths">
								<Form.Item
									label={t('single.months')}
									//tooltip="Select the months when this sprint should recur."
									tooltip={{
										title: t('single.select-months'),
										icon: <InfoCircleOutlined />
									}}
									name="recurrenceMonths"
									rules={[
										{
											required: true,
											message: t('single.months-required')
										}
									]}
								>
									<Checkbox.Group
										options={filteredMonthsOptions}
									/>
								</Form.Item>
							</div>
							<div id="target-recurrenceformitems-recurrencedayofmonth">
								<Form.Item
									label={t('single.day-of-month')}
									//tooltip="Select the day of the month when this sprint should recur."
									tooltip={{
										title: t('single.select-day-of-month2'),
										icon: <InfoCircleOutlined />
									}}
									name="recurrenceDayOfMonth"
									rules={[
										{
											required: true,
											message: t(
												'single.days-of-month-required'
											)
										}
									]}
								>
									<DaysOfMonth />
								</Form.Item>
							</div>
						</>
					)}
					<div id="target-recurrenceformitems-recurrencetimes">
						<Form.Item
							label={t('single.time-slots.label')}
							//tooltip="Select the times of day between start and end of this sprint."
							tooltip={{
								title: t(
									'single.time-slots.instruction%_sprint'
								),
								icon: <InfoCircleOutlined />
							}}
							name="recurrenceTimes"
						>
							<TimesRange />
						</Form.Item>
					</div>
				</>
			)}
		</Space>
	)
})
